import { combineReducers, configureStore } from '@reduxjs/toolkit';

import { withFollowReducer } from '@exp/redux/following';
import testReducer, { defaultState as defaultTestState } from '@exp/redux/TestSlice';

import addressSlice from './address';
import applicationSlice from './application';
import categorySlice from './category';
import communitySlice from './community';
import contextSlice, { initialState as initialContextState } from './context';
import historySlice from './history';
import orgSlice from './org';
import userSlice from './user';
import personalizationSlice from './personalization';

// Defensive fallback: if the host page hasn't already established window.tests
// (e.g. Storybook, standalone testing, a host with no AB-test wiring yet),
// install a plain bag so QA/an AB-test vendor can still call
// window.tests.configure(...) before the widget mounts (see TeamFinder /
// GroupSearchQualifyPage). Never overwrites an existing implementation — most
// host pages already own window.tests for their own AB tests, and this widget
// must not hijack that.
window.tests = window.tests || {};
window.tests.configure =
  window.tests.configure ||
  ((key, config = {}) => {
    window.tests[key] = config;
  });
window.tests.get = window.tests.get || ((key) => window.tests[key]);
window.tests.remove =
  window.tests.remove ||
  ((key) => {
    delete window.tests[key];
  });

const rootReducer = combineReducers({
  address: addressSlice.reducer,
  application: applicationSlice.reducer,
  category: categorySlice.reducer,
  community: communitySlice.reducer,
  context: contextSlice.reducer,
  history: historySlice.reducer,
  org: orgSlice.reducer,
  personalization: personalizationSlice.reducer,
  test: testReducer,
  user: userSlice.reducer,
  ...withFollowReducer(),
});

// Snapshot whatever's currently in window.tests (the host page's AB-test bag —
// see TeamFinder/GroupSearchQualifyPage) at store-creation time — read fresh on
// every call, since a new store is created per widget `.show()`. ERC's
// defaultTestState only strips out its own named configure/remove functions, so
// filter out ALL function values ourselves first (the bag may also carry a get()).
const createPreloadedState = (config: Partial<RootState['context']>): Partial<RootState> => {
  const testConfigEntries = Object.entries(window.tests || {}).filter(([, value]) => typeof value !== 'function');
  return {
    context: initialContextState(config),
    test: defaultTestState(Object.fromEntries(testConfigEntries)),
  };
};

const isDev = process.env.NODE_ENV !== 'production';

const createStore = ({ config = {}, middleware: customMiddleware = [] }) =>
  configureStore({
    ...(isDev && { devTools: { name: 'exp-onboarding' } }),
    middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(customMiddleware),
    preloadedState: createPreloadedState(config),
    reducer: rootReducer,
  });

export type PreloadedState = Parameters<typeof rootReducer>[0];
export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof createStore>;
export type AppDispatch = AppStore['dispatch'];

export default createStore;
